import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Baby,
  Search,
  Sparkles,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Heart,
  ShieldAlert,
  Activity,
  Brain,
  Droplet,
  Zap,
  Bug
} from 'lucide-react';

import { API_URL } from '../config';

export default function MedicamentsPediatriePage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [aiLoading, setAiLoading] = useState({});
  const [aiResults, setAiResults] = useState({});

  const handleAiAnalysis = async (medicationId, medicationName, content) => {
    setAiLoading({ ...aiLoading, [medicationId]: true });
    
    try {
      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: 'medicaments_pediatrie',
          audience: 'pediatric',
          prompt: `Analyse pharmaceutique pédiatrique pour ${medicationName}:

${content.slice(0, 500)}

Fournis une analyse synthétique avec:
1. Particularités posologiques en pédiatrie (doses poids/âge)
2. Préparations et dilutions adaptées
3. Contre-indications spécifiques pédiatriques
4. Précautions selon l'âge
5. Surveillance clinique et biologique nécessaire`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResults({ ...aiResults, [medicationId]: data.response });
        toast.success(`Analyse AI ${medicationName} complétée`);
      } else {
        toast.error('Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setAiLoading({ ...aiLoading, [medicationId]: false });
    }
  };

  // Dataset complet des médicaments pédiatriques
  const medications = [
    {
      id: 'aciclovir',
      commercial: 'ZOVIRAX®',
      dci: 'Aciclovir',
      indication: 'Infection à virus Varicelle-Zona (VZV) et Herpès Simplex (HSV), Méningo-encéphalite herpétique',
      preparation: 'Flacon poudre 250 mg. À reconstituer : diluer 250 mg dans 10 ml NaCl 0,9% → 25 mg/ml',
      posologie: `**Dose méningée:**
• **< 12 ans:** 20 mg/kg toutes les 8h (500 mg/m²/8h)
• **> 12 ans:** 15 mg/kg toutes les 8h (max 1,5 g/injection)
• Diluer pour concentration finale ≤ 5 mg/ml
• Administrer en 1 heure

⚠️ **Adapter les doses si insuffisance rénale**

**Contre-indications:**
• Allergie ou hypersensibilité à l'aciclovir

**Effets indésirables:**
• Troubles digestifs: nausées, vomissements, diarrhées
• Augmentation bilirubine et enzymes hépatiques
• Prurit, urticaire`
    },
    {
      id: 'adenosine',
      commercial: 'KRENOSIN®',
      dci: 'Adénosine',
      indication: 'Conversion des tachycardies jonctionnelles en rythme sinusal. Aide au diagnostic des tachycardies',
      preparation: 'Flacon 6 mg/2 ml (3 mg/ml). Poids < 30 kg pour 1ère dose et < 20 kg pour 2ème dose: diluer 6 mg dans 12 ml NaCl 0,9% → 0,5 mg/ml',
      posologie: `**Posologie:**
• **1ère dose:** 0,1 mg/kg IVD
• **Puis:** 0,3 mg/kg à renouveler 2-3 fois si échec (toutes les 3 min)
• **Injection:** Molécule PUIS 5 ml NaCl 0,9% "FLASH"

⚠️ **Prudence chez l'enfant asthmatique**

**Contre-indications:**
• Hypersensibilité au KRENOSIN®
• BAV 2 et 3 (sauf stimulateur cardiaque)
• Hypotension artérielle sévère

**Effets indésirables:**
• TF: pause sinusale, syncope, bradycardie
• Dyspnée, vertiges, céphalée
• Sensation d'oppression thoracique, flush facial
• Rare: asystolie`
    },
    {
      id: 'adenosine-triphosphate',
      commercial: 'STRIADYNE®',
      dci: 'Adénosine triphosphate',
      indication: 'Conversion des tachycardies jonctionnelles. Aide au diagnostic des tachycardies',
      preparation: 'Ampoule 20 mg/2 ml (10 mg/ml). Poids < 20 kg: diluer 20 mg dans 10 ml NaCl 0,9% → 2 mg/ml',
      posologie: `**Posologie:**
• **1ère dose:** 0,5 mg/kg IVD
• **2ème dose (à 3 min):** 1 mg/kg
• **Injection:** Molécule PUIS 5 ml NaCl 0,9% "FLASH"

**Contre-indications:**
• Bronchospasme: asthme sévère, BPCO
• Hypersensibilité connue à l'adénosine

**Effets indésirables:**
• TF: pause sinusale, syncope, bradycardie, asystolie
• Extrasystole auriculaire et ventriculaire
• BAV, dyspnée, flush facial
• Sensation d'oppression thoracique, anxiété, céphalée`
    },
    {
      id: 'alprostadil',
      commercial: 'PROSTINE®',
      dci: 'Alprostadil',
      indication: 'Ouverture du canal artériel, cardiopathies ductodépendantes',
      preparation: 'Solution injectable 0,5 mg/1 ml. Diluer 0,5 ampoule (250 µg/0,5 ml) dans 50 ml SG5% → 5 µg/ml',
      posologie: `**Posologie:**
• **Dose de charge:** 1 ml/kg/h (0,08 µg/kg/min) en 30 min
• **Dose d'entretien:** 0,5 ml/kg/h
• **Si apnée:** Réduire de moitié à 0,25 ml/kg/h

**Effets indésirables:**
• Apnée (fréquent)
• Fièvre, douleur, agitation
• Vasodilatation cutanée, œdème`
    },
    {
      id: 'amiodarone-po',
      commercial: 'CORDARONE® (PER OS)',
      dci: 'Amiodarone',
      indication: 'Troubles du rythme',
      preparation: 'Comprimé 200 mg sécable. Poids < 8 kg: dissoudre dans 4 ml eau ou lait → 50 mg/ml',
      posologie: `**Posologie:**
• **Dose de charge:** 500 mg/m² 3-5 jours (m² = (4P+7)/(P+90))
• **Dose entretien:** 250 mg/m²

**Contre-indications:**
• Bradycardie, BAV sévère
• Hypotension artérielle sévère
• Dysthyroïdie
• Relative: grossesse
• **Aucune CI si AC**

**Effets indésirables:**
• TF: nausées
• F: bradycardie, hypotension, dysthyroïdie

**IPC:** Héparine sodique, digoxine, furosémide`
    },
    {
      id: 'amiodarone-iv',
      commercial: 'CORDARONE® IV',
      dci: 'Amiodarone',
      indication: 'AC avec rythme chocable: FV, TV sans pouls',
      preparation: 'Ampoule 150 mg/3 ml (50 mg/ml). Diluer 2 ampoules (300 mg) dans 12 ml NaCl 0,9% → 25 mg/ml',
      posologie: `**Posologie:**
• **5 mg/kg** après 3ème et 5ème CEE
• **Voie:** IVD PUIS flash 5 ml NaCl 0,9%
• **Privilégier:** Voie intraosseuse (VVP possible si pose 1er essai)

**Note:** Pas de contre-indication en cas d'AC`
    },
    {
      id: 'augmentin',
      commercial: 'AUGMENTIN®',
      dci: 'Amoxicilline - Acide clavulanique',
      indication: 'Infections aux germes sensibles à l\'amoxicilline-acide clavulanique',
      preparation: 'Flacon poudre 500 mg amoxicilline + 50 mg acide clavulanique. Reconstituer dans 10 ml EPPI → 50 mg/ml',
      posologie: `**Posologie:**
• **150 mg/kg/j** en 3 injections/24h (à adapter selon pathologie)
• **Nouveau-né 3-4 kg:** 100 mg/kg/j en 2 injections
• **Dose max:** 6 g/j
• **Perfusion:** 30 min

**Contre-indications:**
• Hypersensibilité pénicilline ou acide clavulanique
• ATCD atteinte hépatique liée à l'Augmentin
• Mononucléose infectieuse

**Effets indésirables:**
• F: candidose cutanéo-muqueuse
• F: diarrhées
• Réaction allergique`
    },
    {
      id: 'aspirine',
      commercial: 'KARDEGIC® / ASPIRINE®',
      dci: 'Aspirine',
      indication: 'Kawasaki, cardiopathie cyanogène',
      preparation: 'Sachet 75/160/300 mg ou 100/300/500 mg. Dose anti-agrégante: sachet 100 mg à diluer dans 1 ml',
      posologie: `**Posologie:**
• **Dose anti-agrégante:** 5-10 mg/kg/j en 1 prise
• **Dose anti-inflammatoire (Kawasaki):** 50-80 mg/kg/j en 4 prises pendant 7 jours

**Surveillance:**
• NFS
• Pas de sur-risque syndrome Reye ou varicelle en dose anti-agrégante

**Contre-indications:**
• Hémorragie
• Déficit G6PD
• Syndrome de Reye ou maladie métabolique
• CI association AINS si dose anti-inflammatoire

**Effets indésirables:**
• Thrombopénie, saignement, allergie`
    },
    {
      id: 'atropine',
      commercial: 'ATROPINE®',
      dci: 'Atropine sulfate',
      indication: 'Bradycardie mal tolérée. Troubles conduction auriculo-ventriculaire. Prévention bradycardies réflexes intubation < 2 ans',
      preparation: 'Ampoule 0,5 mg/ml ou 1 mg/ml. Poids ≤ 20 kg: diluer 1 mg dans 10 ml NaCl 0,9% → 0,1 mg/ml. Poids < 5 kg: seringue tuberculine',
      posologie: `**Posologie:**
• **0,02 mg/kg** IVD flash
• À renouveler si besoin

**Surveillance:**
• Scope, PA, FC, FR, SpO2
• ECG

**Contre-indications:**
• Aucune CI en cas de détresse vitale
• Hypersensibilité à l'un des constituants

**Effets indésirables:**
• Sécheresse buccale
• Trouble accommodation, diminution sécrétion lacrymale
• Rétention d'urine
• Palpitations, tachycardie, arythmie
• Risque arythmie ventriculaire sur coronaropathie`
    },
    {
      id: 'bicarbonate',
      commercial: 'BICARBONATE®',
      dci: 'Bicarbonate de sodium',
      indication: 'Intoxication grave produits effet stabilisant membrane. AC sur hyperkaliémie si IR et acidose sévère. Acidocétose diabétique PH < 6,8',
      preparation: 'Ampoule 10 ml/flacon 250 ml (0,5 mmol/ml). PUR',
      posologie: `**Posologie:**
• **2 ml/kg**
• **Intoxications:** IVL, renouveler jusqu'à QRS fin
• **Acidocétose diabétique:** 45 min

**Surveillance:**
• Scope PA, FC, SpO2
• ECG continu, élargissement QRS
• Gaz du sang, ionogramme sanguin

**Contre-indications:**
• Alcalose métabolique
• Acidose respiratoire

**IPC:** Nutrition parentérale, magnésium, phosphates, calcium, amines

**Effets indésirables:**
• Alcalose métabolique et hypokaliémie si apport excessif
• Risque surcharge sodique`
    },
    {
      id: 'bupivacaine',
      commercial: 'MARCAINE®',
      dci: 'Bupivacaïne',
      indication: 'Analgésie loco-régionale. Bloc ilio-facial, bloc 3 en 1 (fracture fémur) chez enfant vigile. Par médecin formé',
      preparation: 'Ampoule 10 ml ou flacon 20 ml (2,5 mg/ml). PUR ou avant 6 mois: diluer 5 ml à 0,25% dans 10 ml NaCl 0,9% → 1,25 mg/ml',
      posologie: `**Posologie:**
• **Poids ≤ 10 kg:** 1 ml/kg Bupivacaïne 2,5 mg/ml OU 2 ml/kg dilué 1,25 mg/ml
• **11-25 kg:** 0,8 ml/kg Bupivacaïne 2,5 mg/ml
• **Poids ≥ 25 kg:** 0,5 ml/kg Bupivacaïne 2,5 mg/ml (max 35 ml)

**Technique:**
• Vérifier absence reflux sanguin
• Injection lente et fractionnée
• Arrêter si effets secondaires

**Surveillance:**
• Scope, SpO2, FR, conscience, état neurologique, convulsions
• Matériel réanimation à proximité

**Contre-indications:**
• Allergie anesthésiants locaux
• Patient instable
• Troubles hémostase, infection locale

**Effets indésirables:**
• GRAVE: toxicité systémique AL (cardiaques et neurologiques)
• Si EI graves: RCP, anticonvulsivants, émulsion lipidique 20%: bolus 1,5 ml/kg puis PSE 0,5-1 ml/kg/min (max 10 ml/kg)`
    },
    {
      id: 'calcium',
      commercial: 'CHLORURE DE CALCIUM®',
      dci: 'Chlorure de calcium',
      indication: 'Hypocalcémie aiguë. Hyperkaliémie menaçante. Intoxication inhibiteurs calciques. Transfusion massive',
      preparation: 'Ampoule 10 ml à 10% (1 g). PUR',
      posologie: `**Posologie:**
• **20 mg/kg** soit **0,2 ml/kg**
• **Ne jamais dépasser 1 ampoule**

**Surveillance:**
• Scope
• Défibrillateur à proximité (risque TDR sur hyperkaliémie)
• Ionogramme sanguin

**Contre-indications:**
• Hypercalcémie, néphrocalcinose
• Hypercalciurie
• Risque TDR grave si association digitaliques

**IPC:** Nutrition parentérale, sulfate magnésium, propofol, midazolam, pantoprazole, vancomycine, ceftriaxone

**Effets indésirables:**
• Risque calcifications vasculaires SC/viscérales si perfusion prolongée
• Risque nécrose tissulaire`
    },
    {
      id: 'captopril',
      commercial: 'NOYADA®',
      dci: 'Captopril',
      indication: 'Insuffisance cardiaque et HTA',
      preparation: 'Suspension buvable 25 mg/5 ml ou 5 mg/5 ml. Comprimé LOPRIL® 25, 50, 100 mg',
      posologie: `**Posologie:**
• **Débuter:** 0,5 mg/kg/j en 3 prises
• **Augmentation:** Par palier de 0,5 mg/kg/j selon tolérance
• **Objectif:** Jusqu'à 3 mg/kg/j

**Surveillance:**
• PA avant et 30 min après prise pendant 24h suivant augmentation
• Baisser dose si TAS chute > 20 mmHg
• Iono toutes les 48-72h après chaque palier
• Kaliémie, fonction rénale

**Contre-indications:**
• Diurétique hyperkaliémiant (précaution)
• Médicaments leucopéniants

**Effets indésirables:**
• Hypotension, éosinophilie, toux
• Neutropénie voire agranulocytose, agueusie
• Protéinurie voire syndrome néphrotique`
    }
  ];

  // Filtrer les médicaments selon le terme de recherche
  const filteredMedications = medications.filter(med => {
    const searchLower = searchTerm.toLowerCase();
    return (
      med.commercial.toLowerCase().includes(searchLower) ||
      med.dci.toLowerCase().includes(searchLower) ||
      med.indication.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl" data-testid="medicaments-pediatrie-container">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-br from-pink-500/20 to-purple-500/20 rounded-xl border border-pink-400/30">
            <Baby className="w-8 h-8 text-pink-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[hsl(var(--text))]">Médicaments Pédiatrie</h1>
            <p className="text-[hsl(var(--text-dim))]">Guide posologique pédiatrique d'urgence - SMUR 2025</p>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--text-dim))]" />
          <Input
            type="text"
            placeholder="Rechercher un médicament par nom, DCI ou indication..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-3 text-base bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]"
            data-testid="medication-search-input"
          />
        </div>

        {/* Results Count */}
        {searchTerm && (
          <p className="mt-3 text-sm text-[hsl(var(--text-dim))]">
            {filteredMedications.length} médicament{filteredMedications.length > 1 ? 's' : ''} trouvé{filteredMedications.length > 1 ? 's' : ''}
          </p>
        )}

        {/* Quick Access Links to Protocols */}
        <div className="mt-6">
          <h2 className="text-lg font-semibold text-[hsl(var(--text))] mb-3">Accès rapide aux protocoles pédiatriques</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <Link 
              to="/protocols/69365c2f98c6705b68ab375c" 
              className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-red-500/10 to-red-600/10 hover:from-red-500/20 hover:to-red-600/20 border border-red-400/30 rounded-lg transition-all hover:scale-105"
            >
              <Heart className="w-6 h-6 text-red-400" />
              <span className="text-sm font-medium text-center text-[hsl(var(--text))]">Arrêt cardiaque</span>
            </Link>

            <Link 
              to="/protocols/69365c2f98c6705b68ab375b" 
              className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-orange-500/10 to-orange-600/10 hover:from-orange-500/20 hover:to-orange-600/20 border border-orange-400/30 rounded-lg transition-all hover:scale-105"
            >
              <ShieldAlert className="w-6 h-6 text-orange-400" />
              <span className="text-sm font-medium text-center text-[hsl(var(--text))]">Anaphylaxie</span>
            </Link>

            <Link 
              to="/protocols" 
              className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-blue-500/10 to-blue-600/10 hover:from-blue-500/20 hover:to-blue-600/20 border border-blue-400/30 rounded-lg transition-all hover:scale-105"
            >
              <Activity className="w-6 h-6 text-blue-400" />
              <span className="text-sm font-medium text-center text-[hsl(var(--text))]">Douleur</span>
            </Link>

            <Link 
              to="/protocols/69365c2f98c6705b68ab375d" 
              className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 hover:from-yellow-500/20 hover:to-yellow-600/20 border border-yellow-400/30 rounded-lg transition-all hover:scale-105"
            >
              <Brain className="w-6 h-6 text-yellow-400" />
              <span className="text-sm font-medium text-center text-[hsl(var(--text))]">Épilepsie</span>
            </Link>

            <Link 
              to="/protocols" 
              className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-purple-500/10 to-purple-600/10 hover:from-purple-500/20 hover:to-purple-600/20 border border-purple-400/30 rounded-lg transition-all hover:scale-105"
            >
              <Droplet className="w-6 h-6 text-purple-400" />
              <span className="text-sm font-medium text-center text-[hsl(var(--text))]">Choc</span>
            </Link>

            <Link 
              to="/protocols/69365c2f98c6705b68ab3759" 
              className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-violet-500/10 to-violet-600/10 hover:from-violet-500/20 hover:to-violet-600/20 border border-violet-400/30 rounded-lg transition-all hover:scale-105"
            >
              <Zap className="w-6 h-6 text-violet-400" />
              <span className="text-sm font-medium text-center text-[hsl(var(--text))]">AVC</span>
            </Link>

            <Link 
              to="/antibio" 
              className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-green-500/10 to-green-600/10 hover:from-green-500/20 hover:to-green-600/20 border border-green-400/30 rounded-lg transition-all hover:scale-105"
            >
              <Bug className="w-6 h-6 text-green-400" />
              <span className="text-sm font-medium text-center text-[hsl(var(--text))]">Infection</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-[hsl(var(--text))]">
            <Baby className="w-5 h-5 text-pink-400" />
            Pharmacopée Pédiatrique
          </CardTitle>
          <CardDescription>
            Cliquez sur un médicament pour afficher les informations détaillées (posologies adaptées au poids/âge)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px] pr-4">
            {filteredMedications.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-orange-400 mx-auto mb-4" />
                <p className="text-[hsl(var(--text-dim))]">Aucun médicament trouvé</p>
                <p className="text-sm text-[hsl(var(--text-dim))] mt-2">
                  Essayez un autre terme de recherche
                </p>
              </div>
            ) : (
              <Accordion type="single" collapsible className="w-full space-y-4">
                {filteredMedications.map((medication) => {
                  const isLoading = aiLoading[medication.id];
                  const aiResult = aiResults[medication.id];

                  return (
                    <AccordionItem
                      key={medication.id}
                      value={medication.id}
                      className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]"
                      data-testid={`medication-item-${medication.id}`}
                    >
                      <AccordionTrigger
                        className="px-6 py-4 hover:no-underline hover:bg-[hsl(var(--bg-elev))] transition-colors"
                        data-testid={`accordion-trigger-${medication.id}`}
                      >
                        <div className="flex flex-col items-start gap-1 w-full text-left">
                          <div className="flex items-center gap-2">
                            <Baby className="w-4 h-4 text-pink-400" />
                            <span className="font-semibold text-[hsl(var(--text))]">
                              {medication.commercial}
                            </span>
                          </div>
                          <span className="text-sm text-[hsl(var(--text-dim))]">
                            {medication.dci}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 py-4 space-y-4">
                        <div className="space-y-4">
                          {/* Medication Details */}
                          <div className="space-y-3">
                            <div>
                              <h4 className="text-sm font-semibold text-pink-400 mb-1">DCI:</h4>
                              <p className="text-[hsl(var(--text))]">{medication.dci}</p>
                            </div>
                            
                            <div>
                              <h4 className="text-sm font-semibold text-pink-400 mb-1">Indication:</h4>
                              <p className="text-[hsl(var(--text))]">{medication.indication}</p>
                            </div>
                            
                            <div>
                              <h4 className="text-sm font-semibold text-pink-400 mb-1">Préparation:</h4>
                              <p className="text-[hsl(var(--text))]">{medication.preparation}</p>
                            </div>
                            
                            <div>
                              <h4 className="text-sm font-semibold text-pink-400 mb-1">Posologie:</h4>
                              <div className="prose prose-invert max-w-none">
                                <div className="text-sm text-[hsl(var(--text))] whitespace-pre-wrap leading-relaxed">
                                  {medication.posologie.split('\n').map((line, idx) => {
                                    if (line.startsWith('**') && line.endsWith('**')) {
                                      return (
                                        <h5 key={idx} className="text-base font-semibold text-pink-400 mt-3 mb-1">
                                          {line.replace(/\*\*/g, '')}
                                        </h5>
                                      );
                                    }
                                    if (line.startsWith('• ')) {
                                      return (
                                        <div key={idx} className="flex items-start gap-2 my-1">
                                          <CheckCircle2 className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" />
                                          <span>{line.substring(2)}</span>
                                        </div>
                                      );
                                    }
                                    if (line.startsWith('⚠️')) {
                                      return (
                                        <div key={idx} className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-400/30 my-2">
                                          <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                                          <span className="text-orange-300">{line.substring(2)}</span>
                                        </div>
                                      );
                                    }
                                    return line.trim() ? <p key={idx} className="my-1">{line}</p> : <div key={idx} className="h-1" />;
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* AI Analysis Button */}
                          <Button
                            onClick={() => handleAiAnalysis(
                              medication.id, 
                              medication.commercial,
                              `${medication.dci}\n\nIndication: ${medication.indication}\n\nPosologie: ${medication.posologie}`
                            )}
                            disabled={isLoading}
                            className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
                            data-testid={`ai-button-${medication.id}`}
                          >
                            {isLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Analyse AI en cours...
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-4 h-4 mr-2" />
                                Analyse AI de ce médicament
                              </>
                            )}
                          </Button>

                          {/* AI Result */}
                          {aiResult && (
                            <div className="p-6 bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-pink-400/30 rounded-lg">
                              <h4 className="text-lg font-semibold text-pink-400 mb-4 flex items-center gap-2">
                                <Sparkles className="w-5 h-5" />
                                Analyse AI
                              </h4>
                              <div className="prose prose-invert max-w-none">
                                <div 
                                  className="text-[hsl(var(--text))] whitespace-pre-wrap text-sm" 
                                  data-testid={`ai-result-${medication.id}`}
                                >
                                  {aiResult}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
